import { createStyles } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';

import {
  getContestTierCustomNames,
  getTierDisplayLabel,
} from '~/domains/contest/domains/common/utils/playerCardDisplay';
import dt from '~/domains/contest/domains/game/domains/common/testing.const';
import Image from '~/domains/common/components/Image/Image';
import { Text } from '~/domains/design-system/Text';

import playerSillouetteImage from '../../../assets/player-sillouette.svg';
import { useContestContext } from '../../../../common/context/ContestContext/ContestContext';

type PlayerCardHeaderProps = {
  playerImage: string;
  primaryColor?: string;
  tag?: {
    position?: string;
    salary?: number;
    injury?: string;
    playerTier?: string;
    lineup?: {
      order?: number;
      id?: string;
    };
  };
};

const useStyles = createStyles((theme, props: { primaryColor?: string }) => ({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    minWidth: 112,
    minHeight: 112,
    background: `linear-gradient(180deg, ${theme.white} 0%, ${theme.colors.gray2[0]} 100%)`,
    borderTopLeftRadius: theme.other.spacing._8 - 2,
    borderTopRightRadius: theme.other.spacing._8 - 2,

    '&.primaryColor': {
      background: `linear-gradient(180deg, ${theme.white} 0%, ${props?.primaryColor ?? theme.white} 100%)`,
    },
  },
  tag: {
    zIndex: 4,
    minHeight: 24,
    width: 'auto',
    position: 'absolute',
    justifyContent: 'center',
    alignItems: 'center',
    padding: `${theme.other.spacing._2}px ${theme.other.spacing._8}px`,
    background: theme.white,

    '&.bottomLeft': {
      bottom: 0,
      left: 0,
      borderTopRightRadius: theme.other.spacing._8,
    },

    '&.bottomLeft.success': {
      backgroundColor: theme.colors.green[5],
      color: theme.white,
    },

    '&.bottomRight': {
      bottom: 0,
      right: 0,
      borderTopLeftRadius: theme.other.spacing._8,
      backgroundColor: theme.colors.red[3],
      textAlign: 'right',
    },
    '&.topLeft': {
      top: 0,
      left: 0,
      borderBottomRightRadius: theme.other.spacing._8,
    },
  },
  playerImage: {
    objectFit: 'contain',
    objectPosition: 'bottom center',
    zIndex: 2,
    marginTop: theme.other.spacing._12,
  },
}));

export function PlayerCardHeader({ playerImage, tag, primaryColor }: PlayerCardHeaderProps) {
  const { classes, cx } = useStyles({ primaryColor });
  const { contest } = useContestContext();
  const customNames = getContestTierCustomNames(contest);
  const displayedPlayerTier = tag?.playerTier
    ? getTierDisplayLabel(tag.playerTier, customNames, true)
    : undefined;

  return (
    <div
      className={cx(classes.container, { primaryColor: true })}
      data-test-id={dt.dfs.playerCard.header}
    >
      {displayedPlayerTier ? (
        <div className={cx(classes.tag, 'bottomLeft')}>
          <Text variant="headline-small">{displayedPlayerTier || tag.position}</Text>
        </div>
      ) : null}

      {tag?.lineup ? (
        <div className={cx(classes.tag, 'bottomLeft', 'success')}>
          {tag?.lineup?.order ? (
            <Text variant="headline-small">{tag.lineup.order}</Text>
          ) : (
            <IconCheck size={12} strokeWidth={4} />
          )}
        </div>
      ) : null}

      {tag?.injury ? (
        <div className={cx(classes.tag, 'bottomRight')}>
          <Text variant="headline-small" color="white">
            {tag.injury}
          </Text>
        </div>
      ) : null}

      <Image
        src={playerImage}
        alt="Player image"
        width={112}
        height={112}
        fallbackImageUrl={playerSillouetteImage.src}
        className={classes.playerImage}
      />
    </div>
  );
}
